import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { affiliateApi } from "../../../modules/affiliate/api";
import { CreateUpdateAffiliatePartner } from "../../../modules/affiliate/types";
import { EnumerationType } from "../../../modules/enumerations/enums";
import type { UUID } from "../../../typings/global";
import { EntityObject, SearchPageRequest } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const AFFILIATE_QUERY_KEYS = {
  filterAffiliatePartners: (filter?: SearchPageRequest) =>
    filter ? ["filterAffiliatePartners", filter] : ["filterAffiliatePartners"],
  createAffiliatePartner: () => ["createAffiliatePartner"],
  updateAffiliatePartner: () => ["updateAffiliatePartner"],
  deleteAffiliatePartner: () => ["deleteAffiliatePartner"],
};

export const useFilterAffiliatePartners = (filter: SearchPageRequest) => {
  return useQuery({
    queryKey: AFFILIATE_QUERY_KEYS.filterAffiliatePartners(filter),
    queryFn: async () => {
      const { data } = await affiliateApi.filterAffiliatePartners(filter);

      return data;
    },
  });
};

export const useCreateAffiliatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AFFILIATE_QUERY_KEYS.createAffiliatePartner(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateAffiliatePartner) => {
      const { data } = await affiliateApi.createAffiliatePartner(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AFFILIATE_PARTNERS);
      queryClient.invalidateQueries({ queryKey: AFFILIATE_QUERY_KEYS.filterAffiliatePartners() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateAffiliatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AFFILIATE_QUERY_KEYS.updateAffiliatePartner(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateAffiliatePartner>) => {
      const { data } = await affiliateApi.updateAffiliatePartner(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AFFILIATE_PARTNERS);
      queryClient.invalidateQueries({ queryKey: AFFILIATE_QUERY_KEYS.filterAffiliatePartners() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteAffiliatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: AFFILIATE_QUERY_KEYS.deleteAffiliatePartner(),
    mutationFn: (id: UUID) => {
      return affiliateApi.deleteAffiliatePartner({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.AFFILIATE_PARTNERS);
      queryClient.invalidateQueries({ queryKey: AFFILIATE_QUERY_KEYS.filterAffiliatePartners() });
      Notification.itemDeletedNotification();
    },
  });
};
